import React, {FC, useEffect, useState} from 'react';
import InfoPanel from '@/components/Panels/Parcel/InfoPanel';
import {ParcelSummary} from '@/domain/entities/ParcelSummary';
import {Dispute, DisputeStatus} from '@/domain/entities/Dispute';
import DisputePanel from '@/components/Panels/Parcel/DisputePanel';
import Documentation from '@/components/Panels/Parcel/Documentation';
import {DocumentProperties, ParcelDocumentCreation} from '@/domain/entities/ParcelDocument';
import {Project} from '@/domain/entities/Project';
import {User} from '@/domain/entities/User';

//TYPES
export enum SelectedParcelMode {
  INFO = 'info',
  DISPUTE = 'dispute',
  DOCUMENTS = 'documents',
}

export type ParcelPanelProps = {
  project?: Project,
  user?: User,
  dispute?: Dispute,
  parcelSummary?: ParcelSummary,
  documents?: Array<DocumentProperties>,
  onDisputeSaved: (dispute: Omit<Dispute, 'geom'>) => void,
  onDocumentUpload: (dataForUpload: ParcelDocumentCreation) => void,
  onDocumentDownload: (dataUrl: string, name: string) => void,
  onDocumentDelete: (uuid: string) => void,
  toggleGeometryVisibility: (isGeometryVisible: boolean) => void
};

const ParcelPanel: FC<ParcelPanelProps> = ({project, user, dispute, parcelSummary, documents, onDisputeSaved, onDocumentUpload, onDocumentDownload, onDocumentDelete, toggleGeometryVisibility}) => {

  if(!parcelSummary) return;

  const [selectedParcelMode, setSelectedParcelMode] = useState<SelectedParcelMode>(SelectedParcelMode.INFO);

  const selectedParcelUuid = parcelSummary.uuid;

  useEffect(() => {
    if(selectedParcelMode === SelectedParcelMode.DISPUTE){
      toggleGeometryVisibility(true);
    }
  }, [selectedParcelMode]);

  useEffect(() => {
    setSelectedParcelMode(SelectedParcelMode.INFO);
  }, [parcelSummary]);

  const handleDisputeClicked = () => setSelectedParcelMode(SelectedParcelMode.DISPUTE);
  const handleDocumentListClicked = () => setSelectedParcelMode(SelectedParcelMode.DOCUMENTS);

  const handleDisputeExist = () => {
    setSelectedParcelMode(SelectedParcelMode.INFO);
    toggleGeometryVisibility(false);
  };

  const handleDisputeSave = (dispute: Omit<Dispute, 'geom'>) => {
    if(selectedParcelUuid){
      onDisputeSaved({
        uuid: dispute.uuid,
        parcelUuid: selectedParcelUuid,
        status: dispute.status || DisputeStatus.IN_REVIEW,
        description: dispute.description,
        createdAt: dispute.createdAt,
        updatedAt: dispute.updatedAt
      });
    }
  };

  if(selectedParcelMode === SelectedParcelMode.INFO) {
    return <InfoPanel
      project={project}
      user={user}
      dispute={dispute}
      parcelSummary={parcelSummary}
      documents={documents}
      onDisputePanelOpen={handleDisputeClicked}
      onDocumentListPanelOpen={handleDocumentListClicked}
      onSelectedParcelModeChanged={setSelectedParcelMode}
    />;
  }

  if(selectedParcelMode === SelectedParcelMode.DISPUTE) {
    return <DisputePanel
      user={user}
      selectedParcelUuid={selectedParcelUuid}
      parcelSummary={parcelSummary}
      dispute={dispute}
      onDisputeSaved={handleDisputeSave}
      onSelectedParcelModeChanged={handleDisputeExist}/>;
  }

  if(selectedParcelMode === SelectedParcelMode.DOCUMENTS) {
    return <Documentation
      selectedParcelUuid={parcelSummary.uuid}
      documents={documents}
      onDocumentUpload={onDocumentUpload}
      onDocumentDownload={onDocumentDownload}
      onDocumentDelete={onDocumentDelete}
      onSelectedParcelModeChanged={setSelectedParcelMode}
    />;
  }

};
export default ParcelPanel;